import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import Table from "../../core/common/dataTable/index";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import PageHeader from "../../core/common/commonComponents/PageHeader";
import { exportToCSV, exportToExcel } from "../../core/common/GlobalFunctions/exportTableData";
import StatusFilterHeader from "../../core/common/commonComponents/StatusFilterHeader";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../core/data/redux/store";
import DeleteModal1 from "../../core/modals/deleteModal1";
import { message } from "antd";
import { filterData } from "../../core/common/GlobalFunctions/tableFilters";
import { deletePfEmployee, fetchAllPfEmployees } from "../../core/data/redux/payroll/Accounting/providentFund/pfEmployee/pfEmployeeActions";
import PfLoanRequestModal from "./modals/PfLoanRequestModal";
import { deletePfLoan, fetchAllPfLoans } from "../../core/data/redux/payroll/Accounting/providentFund/pfLoanRequest/pfLoanActions";
import { formatCurrency, formatDate } from "../../core/utils/configFormatters";
import { useAppConfig } from "../../customhooks/useAppConfig";

const PfLoanRequest = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const { records } = useSelector((state: any) => state.providentFund.pfLoanRequest);
    const config = useAppConfig();

    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('');
    };
    useEffect(() => {
        if (!records.length) {
            dispatch(fetchAllPfLoans())
        }
    }, [dispatch, records.length])

    const filtereddata = useMemo(() => {
        const filters = selectedStatus && selectedStatus !== "All"
            ? { status: selectedStatus }
            : {};
        return filterData(records, filters, "createdAt", "createdAt", selectedSort);
    }, [records, selectedStatus, selectedSort]);

    const ExportData = filtereddata?.map((item: any) => ({
        pfLoanId: item.pfLoanId || "N/A",
        pfEmployeeId: item.pfEmployeeId || "N/A",
        employeeName: item.employeeName || "N/A",
        requestDate: item.requestDate ? new Date(item.requestDate).toLocaleDateString("en-IN") : "N/A",
        amountRequested: item.amountRequested || 0,
        loanReason: item.loanReason || "N/A",
        requeststatus: item.requeststatus || "Pending",
        repaymentTenureMonths: item.repaymentTenureMonths || 0,
        monthlyInstallment: item.monthlyInstallment || 0,
        status: item.status || "N/A",
        createdBy: item.createdBy || "N/A",
        modifiedBy: item.modifiedBy || "N/A",
        createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-IN") : "N/A",
        modifiedAt: item.modifiedAt ? new Date(item.modifiedAt).toLocaleDateString("en-IN") : "N/A",
    }));


    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedStatus, selectedSort, records]);

    const PfLoanDelete = async (id: number) => {
        try {
            const result = await dispatch(deletePfLoan(id));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Loan Request deleted successfully');
                setTableKey((prevKey: any) => prevKey + 1);
                setTableKey((prevKey) => prevKey + 1);
                setIsDeleteModalVisible(false);
            } else {
                message.error((result.payload as { error: string })?.error);
            }
        } catch (error) {
            message.error('Something went wrong');
        }
    };

    const columns = [
        {
            title: "Employee",
            dataIndex: "employeeName",
            render: (_: any, record: any) => (
                <div
                    className="d-flex align-items-center"
                    style={{ minWidth: 200, gap: 8 }}
                    onMouseEnter={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "1")}
                    onMouseLeave={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "0")}
                >
                    <img
                        src={record.employeePictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt={record.employeeName}
                        style={{ width: 32, height: 32, borderRadius: "50%" }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = "/default-avatar.png";
                        }}
                    />
                    <span>{record.employeeName}</span>
                    <button
                        className="btn btn-link p-0"
                        onClick={() => {
                            showModal("edit");
                            setSelectedRowData(record);
                        }}
                        style={{
                            cursor: "pointer",
                            opacity: 0, // hidden by default
                            transition: "opacity 0.3s",
                        }}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Edit"
                    >
                        <i className="ti ti-edit" />
                    </button>
                </div>
            ),
            sorter: (a: any, b: any) => a.employeeName?.localeCompare(b.employeeName),
        },
        {
            title: "Request Date",
            dataIndex: "requestDate",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime(),
        },
        {
            title: "Amount Requested",
            dataIndex: "amountRequested",
            render: (text: string) => formatCurrency(parseFloat(text || '0'), config),
            sorter: (a: any, b: any) => parseFloat(a.amountRequested) - parseFloat(b.amountRequested),
        },
        {
            title: "Loan Reason",
            dataIndex: "loanReason",
            sorter: (a: any, b: any) => a.loanReason?.localeCompare(b.loanReason),
        },
        {
            title: "Tenure (Months)",
            dataIndex: "repaymentTenureMonths",
            sorter: (a: any, b: any) => a.repaymentTenureMonths - b.repaymentTenureMonths,
        },
        {
            title: "Installment (₹)",
            dataIndex: "monthlyInstallment",
            render: (text: string) => formatCurrency(parseFloat(text || '0'), config),
            sorter: (a: any, b: any) => parseFloat(a.monthlyInstallment) - parseFloat(b.monthlyInstallment),
        },
        {
            title: "Request Status",
            dataIndex: "requeststatus",
            filters: [
                { text: "Pending", value: "pending" },
                { text: "Approved", value: "Approved" },
                { text: "Rejected", value: "Rejected" },
                { text: "Disbursed", value: "Disbursed" },
                { text: "Processing", value: "Processing" },
            ],
            onFilter: (value: string, record: any) => record.requeststatus === value,
            render: (text: string) => {
                const statusColorMap: Record<string, string> = {
                    pending: "warning",
                    Approved: "success",
                    Rejected: "danger",
                    Disbursed: "info",
                    Processing: "primary",
                };
                const badgeClass = statusColorMap[text] || "secondary";
                return (
                    <span className={`badge bg-${badgeClass} text-capitalize`}>
                        <i className="ti ti-point-filled me-1" />
                        {text}
                    </span>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text: string) => (
                <span
                    className={`badge ${text === "active" ? "badge-success" : "badge-danger"
                        } d-inline-flex align-items-center badge-xs`}
                >
                    <i className="ti ti-point-filled me-1" />
                    {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

                </span>
            ),
            filters: [
                { text: "Active", value: "active" },
                { text: "Inactive", value: "inactive" },
            ],
            onFilter: (value: string, record: any) => record.status === value,
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (_: any, record: any) => (
                <div className="action-icon d-inline-flex">
                    <button
                        className="me-2"
                        onClick={() => {
                            showModal("edit");
                            setSelectedRowData(record);
                        }} data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Edit"
                    >
                        <i className="ti ti-edit" />
                    </button>
                    <button
                        onClick={() => {
                            setSelectedRowData(record);
                            setIsDeleteModalVisible(true);
                        }} data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Delete"
                    >
                        <i className="ti ti-trash" />
                    </button>
                </div>
            ),
        },
    ];


    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <PageHeader
                        title="Loan Request"
                        breadcrumbs={[
                            { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
                            { label: "Accounting" },
                            { label: "Loan Request List", isActive: true },
                        ]}
                        // listViewPath={all_routes.clientlist}
                        listViewPath="#"
                        onExportCSV={() => exportToCSV(ExportData, 'PFEmployeeMappingData')}
                        onExportExcel={() => exportToExcel(ExportData, 'PFEmployeeMappingData')}
                        onAdd={() => showModal('create')}
                        addLabel="Add Loan Request"
                        exportEnabled
                        addButtonEnabled
                        rightExtra={<CollapseHeader />}
                    />
                    {/* /Breadcrumb */}
                    <div className="card">
                        <StatusFilterHeader
                            title="Loan Request List"
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            selectedSort={selectedSort}
                            setSelectedSort={setSelectedSort}
                        />
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filtereddata} columns={columns} Selection={false} />
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            <PfLoanRequestModal modalType={modalType} handleCancel={handleCancel} isModalVisible={isModalVisible} selectedRowData={selectedRowData} setTableKey={setTableKey} />
            <DeleteModal1
                isVisible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onConfirm={() => selectedRowData?.pfLoanId && PfLoanDelete(selectedRowData.pfLoanId)}
            />
        </>
    );
};
export default PfLoanRequest;
